// SubscriberClubManager.jsx
import React, { useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  deleteDoc,
  addDoc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';

import BunnyUploader from "../services/BunnyUploader";
import { toast } from "react-toastify";

const DEFAULT_CLUB_FORM = {
  welcomeMessage: '',
  quoteOfWeek: '',
  isHolidaySpecial: false,
  holidaySpecial: {
    title: '',
    imageUrl: '',
    audioUrl: '',
    scheduledAt: ''
  },
  sneakPeeks: []
};

function normalizeClubForm(data = {}) {
  // Do not spread legacy `sneakPeek` onto form state — it was re-saved on every
  // update and the Flutter app reads `sneakPeeks` only. Migrate once into array.
  const { sneakPeek: legacySneakPeek, ...rest } = data;
  return {
    ...DEFAULT_CLUB_FORM,
    ...rest,
    sneakPeeks: Array.isArray(data.sneakPeeks)
      ? data.sneakPeeks
      : (legacySneakPeek ? [legacySneakPeek] : []),
    holidaySpecial: {
      ...DEFAULT_CLUB_FORM.holidaySpecial,
      ...(data.holidaySpecial || {})
    }
  };
}

// Sub-component for Bonus Scene to prevent full-page re-renders on typing
const BonusSceneItem = ({ scene, onUpdate, onDelete, books }) => {
  const [localTitle, setLocalTitle] = useState(scene.title);
  const [localAuthor, setLocalAuthor] = useState(scene.author);

  // Sync local state when external scene changes (e.g. from loadData)
  useEffect(() => {
    setLocalTitle(scene.title);
  }, [scene.title]);

  useEffect(() => {
    setLocalAuthor(scene.author);
  }, [scene.author]);

  return (
    <div className="border border-gray-200 p-6 rounded-xl bg-gray-50/50 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <input
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg font-medium"
            placeholder="Scene Title"
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            onBlur={(e) => onUpdate(scene.id, 'title', e.target.value)}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Author Name</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Author Name"
                value={localAuthor}
                onChange={(e) => setLocalAuthor(e.target.value)}
                onBlur={(e) => onUpdate(scene.id, 'author', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Linked Audiobook</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={scene.selectedBookId || ''}
                onChange={(e) => onUpdate(scene.id, 'selectedBookId', e.target.value)}
              >
                <option value="">-- Choose Book --</option>
                {books.map(b => (
                  <option key={b.id} value={b.id}>{b.title}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Scene Image */}
      <div className="mb-6">
        <label className="block text-lg font-medium text-gray-700 mb-2">Scene Image</label>
        <div className="flex gap-3 items-center">
          <input
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) onUpdate(scene.id, 'imageFile', file);
            }}
            className="hidden"
            id={`sceneImageUpload-${scene.id}`}
          />
          <label
            htmlFor={`sceneImageUpload-${scene.id}`}
            className="bg-gray-200 hover:bg-gray-300 px-4 py-2.5 rounded-lg cursor-pointer border border-gray-300 transition-colors font-medium"
          >
            {scene.imageFile ? 'Change Image' : 'Upload Image'}
          </label>
          <input
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Or paste image URL"
            value={scene.imageUrl || ''}
            onChange={(e) => onUpdate(scene.id, 'imageUrl', e.target.value)}
          />
          {(scene.imageFile || scene.imageUrl) && (
            <button
              onClick={() => onUpdate(scene.id, 'imageUrl', '')}
              className="text-red-500 hover:text-red-700 px-3 py-2"
            >
              Clear
            </button>
          )}
        </div>
        {scene.imageUrl && (
          <div className="mt-3">
            <img src={scene.imageUrl} alt="Preview" className="max-h-60 rounded-lg border shadow-sm" />
          </div>
        )}
      </div>

      {/* Scene Audio */}
      <div className="mb-6">
        <label className="block text-lg font-medium text-gray-700 mb-2">Scene Audio</label>
        <div className="flex gap-3 items-center">
          <input
            type="file"
            accept="audio/*"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) {
                const ext = file.name.toLowerCase().split('.').pop();
                if (ext === 'aiff' || ext === 'aif') {
                  toast.error("AIFF files are not supported on Android. Please upload MP3 or WAV.");
                  e.target.value = '';
                  return;
                }
                onUpdate(scene.id, 'audioFile', file);
              }
            }}
            className="hidden"
            id={`sceneAudioUpload-${scene.id}`}
          />
          <label
            htmlFor={`sceneAudioUpload-${scene.id}`}
            className="bg-gray-200 hover:bg-gray-300 px-4 py-2.5 rounded-lg cursor-pointer border border-gray-300 transition-colors font-medium"
          >
            {scene.audioFile ? 'Change Audio' : 'Upload Audio'}
          </label>
          <input
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Or paste audio URL"
            value={scene.audioUrl || ''}
            onChange={(e) => onUpdate(scene.id, 'audioUrl', e.target.value)}
          />
          {(scene.audioFile || scene.audioUrl) && (
            <button
              onClick={() => onUpdate(scene.id, 'audioUrl', '')}
              className="text-red-500 hover:text-red-700 px-3 py-2"
            >
              Clear
            </button>
          )}
        </div>
        {scene.audioUrl && (
          <div className="mt-3">
            <audio controls src={scene.audioUrl} className="w-full rounded-lg" />
          </div>
        )}
      </div>

      <div className="mt-4 mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date & Time (optional)</label>
        <input
          type="datetime-local"
          className="w-full border p-2 rounded"
          value={scene.scheduledAt || ''}
          onChange={(e) => onUpdate(scene.id, 'scheduledAt', e.target.value)}
          min={new Date().toISOString().slice(0, 16)}
        />
      </div>

      <button
        onClick={() => onDelete(scene.id)}
        className="w-full bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg font-medium transition-colors"
      >
        Delete Scene
      </button>
    </div>
  );
};

export default function SubscriberClubManager() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(DEFAULT_CLUB_FORM);

const [bonusScenes, setBonusScenes] = useState([]);
const [polls, setPolls] = useState([]);
const [posts, setPosts] = useState([]);
const [newPost, setNewPost] = useState({
  description: '',
  imageUrl: '',
  imageFile: null,
  scheduledAt: ''
});
const [newPoll, setNewPoll] = useState({
  question: '',
  multiChoice: false,
  options: [{ key: 'A', label: '', votes: 0 }],
  scheduledAt: ''
});
  const [activePoll, setActivePoll] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);

const [editingPost, setEditingPost] = useState(null);
const [editingDraft, setEditingDraft] = useState({ description: '', imageUrl: '' });
const [books, setBooks] = useState([]);

  // Firestore references
  const clubDocRef = doc(db, 'subscriber_club', 'clubData');
  const scenesColRef = collection(clubDocRef, 'bonusScenes');
  const pollsColRef = collection(clubDocRef, 'poll');
  const postsColRef = collection(clubDocRef, 'posts');
  const activePollDocRef = doc(db, 'subscriber_club', 'clubData', 'poll', 'active');
  
  const auth = getAuth();

  // Load all data on component mount
  useEffect(() => {

    const unsub = onSnapshot(collection(db, "audiobooks"), (snapshot) => {
          const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setBooks(list);
        });
    loadData();
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
  setLoading(true);
  try {
    const [docSnap, scenesSnap, pollsSnap, postsSnap, activePollSnap] = await Promise.all([
      getDoc(clubDocRef),
      getDocs(scenesColRef),
      getDocs(pollsColRef),
      getDocs(postsColRef),
      getDoc(activePollDocRef).catch(() => null),
    ]);

    if (docSnap.exists()) {
      const data = normalizeClubForm(docSnap.data());
      
      // Convert Timestamps to datetime-local string format for multiple sneak peeks
      if (Array.isArray(data.sneakPeeks)) {
        data.sneakPeeks = data.sneakPeeks.map(sp => ({
          ...sp,
          scheduledAt: sp.scheduledAt ? convertTimestampToInputFormat(sp.scheduledAt) : ''
        }));
      }

      if (data.holidaySpecial?.scheduledAt) {
        data.holidaySpecial.scheduledAt = convertTimestampToInputFormat(data.holidaySpecial.scheduledAt);
      }
      
      setForm(data);
    } else {
      setForm(DEFAULT_CLUB_FORM);
    }

    // Convert timestamps for bonus scenes
    const scenesData = scenesSnap.docs.map((doc) => {
      const sceneData = doc.data();
      if (sceneData.scheduledAt) {
        sceneData.scheduledAt = convertTimestampToInputFormat(sceneData.scheduledAt);
      }
      return { id: doc.id, ...sceneData };
    });
    setBonusScenes(scenesData);

    // Convert timestamps for polls
    const pollsData = pollsSnap.docs.map((doc) => {
      const pollData = doc.data();
      if (pollData.scheduledAt) {
        pollData.scheduledAt = convertTimestampToInputFormat(pollData.scheduledAt);
      }
      return { id: doc.id, ...pollData };
    });
    setPolls(pollsData);

    // Convert timestamps for posts
    const postsData = postsSnap.docs.map((doc) => {
      const postData = doc.data();
      if (postData.scheduledAt) {
        postData.scheduledAt = convertTimestampToInputFormat(postData.scheduledAt);
      }
      return { id: doc.id, ...postData };
    });
    setPosts(postsData);

    // Handle active poll
    if (activePollSnap?.exists()) {
      const activePollData = activePollSnap.data();
      if (activePollData.scheduledAt) {
        activePollData.scheduledAt = convertTimestampToInputFormat(activePollData.scheduledAt);
      }
      setActivePoll(activePollData);
      
      // Check if current user has voted
      const user = auth.currentUser;
      if (user) {
        const voteDoc = await getDoc(doc(db, 'subscriber_club', 'clubData', 'pollVotes', user.uid));
        setHasVoted(voteDoc.exists());
      }
    } else {
      setActivePoll(null);
      setHasVoted(false);
    }
  } catch (error) {
    console.error("Error loading data:", error);
  } finally {
    setLoading(false);
  }
};

// Helper function to convert Firestore Timestamp to datetime-local input format
function convertTimestampToInputFormat(timestamp) {
  // If it's already a string (from editing), return it
  if (typeof timestamp === 'string') return timestamp;
  
  // If it's a Firestore Timestamp
  if (timestamp.toDate) {
    const date = timestamp.toDate();
    return date.toISOString().slice(0, 16);
  }
  
  // If it's a Date object
  if (timestamp instanceof Date) {
    return timestamp.toISOString().slice(0, 16);
  }
  
  return '';
}

  // Club data management
  const updateClubDoc = async () => {
  try {
    setLoading(true);
    toast.loading('Saving club configuration...');
    
    // Create a copy of the form data
    const formData = { ...form };
    
    // Handle multiple sneak peeks uploads
    if (Array.isArray(formData.sneakPeeks)) {
      for (let i = 0; i < formData.sneakPeeks.length; i++) {
        const sp = formData.sneakPeeks[i];
        if (sp.imageFile) {
          sp.imageUrl = await BunnyUploader.upload(sp.imageFile);
          sp.imageFile = null;
        }
        if (sp.audioFile) {
          sp.audioUrl = await BunnyUploader.upload(sp.audioFile);
          sp.audioFile = null;
        }
      }
    }

    // Upload holiday special media
    if (form.isHolidaySpecial && form.holidaySpecial.imageFile) {
      const imageUrl = await BunnyUploader.upload(form.holidaySpecial.imageFile);
      formData.holidaySpecial.imageUrl = imageUrl;
      formData.holidaySpecial.imageFile = null;
    }
    if (form.isHolidaySpecial && form.holidaySpecial.audioFile) {
      const audioUrl = await BunnyUploader.upload(form.holidaySpecial.audioFile);
      formData.holidaySpecial.audioUrl = audioUrl;
      formData.holidaySpecial.audioFile = null;
    }

    // Update local state
    setForm(formData);
    
    // Prepare data for Firestore (exclude file objects)
    const firestoreData = {
      ...formData,
      sneakPeeks: formData.sneakPeeks.map(sp => ({
        imageUrl: sp.imageUrl || null,
        audioUrl: sp.audioUrl || null,
        scheduledAt: sp.scheduledAt || '',
      }))
    };

    if (formData.isHolidaySpecial) {
      firestoreData.holidaySpecial = {
        title: formData.holidaySpecial.title || null,
        imageUrl: formData.holidaySpecial.imageUrl || null,
        audioUrl: formData.holidaySpecial.audioUrl || null,
        scheduledAt: formData.holidaySpecial.scheduledAt || ''
      };
    } else {
      // Remove holiday special data if not enabled
      delete firestoreData.holidaySpecial;
    }

    // Legacy singular field — app and admin now use `sneakPeeks` only.
    delete firestoreData.sneakPeek;

    // Clean up undefined/null values
    const cleanData = cleanObject(firestoreData);

    // Save to Firestore
    await setDoc(clubDocRef, cleanData);
    toast.success('Club configuration updated successfully!');
    toast.dismiss();
  } catch (error) {
    console.error("Error updating club data:", error);
    toast.error(`Error: ${error.message}`);
    toast.dismiss();
  } finally {
    setLoading(false);
    toast.dismiss();
  }
};

// Helper function to clean object
function cleanObject(obj) {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      if (typeof value === 'object' && !Array.isArray(value)) {
        acc[key] = cleanObject(value);
      } else {
        acc[key] = value;
      }
    }
    return acc;
  }, {});
}

  // Bonus scenes management
  const handleSceneAdd = async () => {
    const newScene = {
      title: 'New Scene',
      author: '',
      imageUrl: '',
      audioUrl: '',
      createdAt: new Date(),
    };
    try {
      await addDoc(scenesColRef, newScene);
      loadData();
    } catch (error) {
      console.error("Error adding scene:", error);
    }
  };

  const handleSceneUpdate = async (id, field, value) => {
  try {
    // Special handling for file uploads
    if (field === 'imageFile' || field === 'audioFile') {
      try {
        // Show loading toast for file upload
        const loadingToast = toast.loading(`Uploading ${field === 'imageFile' ? 'image' : 'audio'}...`);
        
        // Upload immediately
        const fileUrl = await BunnyUploader.upload(value);
        
        // Update Firestore
        await updateDoc(doc(scenesColRef, id), {
          [field.replace('File', 'Url')]: fileUrl
        });

        // Update local state
        setBonusScenes(prev => prev.map(s => s.id === id ? { ...s, [field.replace('File', 'Url')]: fileUrl, [field]: null } : s));
        
        toast.update(loadingToast, { render: 'Uploaded successfully!', type: 'success', isLoading: false, autoClose: 2000 });
      } catch (err) {
        console.error("Upload failed:", err);
        toast.error(`Upload failed: ${err.message}`);
      }
      return;
    }

    // Convert scheduledAt to Date object if needed
    const firestoreValue = field === 'scheduledAt' 
      ? value instanceof Date ? value : new Date(value)
      : value;

    // Regular field updates (non-file fields)
    await updateDoc(doc(scenesColRef, id), { [field]: firestoreValue });
    
    // Update local state for fields that need immediate UI updates
    if (['title', 'author', 'selectedBookId', 'scheduledAt'].includes(field)) {
      setBonusScenes(prevScenes => 
        prevScenes.map(scene => 
          scene.id === id ? { ...scene, [field]: value } : scene
        )
      );
    }

  } catch (error) {
    console.error(`Error updating ${field}:`, error);
    toast.error(`Failed to update ${field.replace('File', '')}: ${error.message}`);
    throw error; // Re-throw to allow calling code to handle
  }
};


  const handleSceneDelete = async (id) => {
    try {
      await deleteDoc(doc(scenesColRef, id));
      loadData();
    } catch (error) {
      console.error("Error deleting scene:", error);
    }
  };

  // Poll management
  const handlePollAdd = async () => {
    if (!newPoll.question || newPoll.options.some(opt => !opt.label)) {
      alert('Please fill in all poll fields');
      return;
    }

    try {
      await setDoc(activePollDocRef, {
        ...newPoll,
        createdAt: new Date(),
        totalVotes: 0,
      });
      setNewPoll({
        question: '',
        multiChoice: false,
        options: [{ key: 'A', label: '', votes: 0 }],
        scheduledAt: '',
      });
      loadData();
    } catch (error) {
      console.error("Error creating poll:", error);
    }
  };

  const updatePollOption = (index, field, value) => {
    const options = [...newPoll.options];
    options[index] = { ...options[index], [field]: value };
    setNewPoll({ ...newPoll, options });
  };

  const addPollOption = () => {
    const nextKey = String.fromCharCode('A'.charCodeAt(0) + newPoll.options.length);
    setNewPoll({
      ...newPoll,
      options: [...newPoll.options, { key: nextKey, label: '', votes: 0 }],
    });
  };
  
const handlePostAdd = async () => {
  if (!newPost.description) {
    alert('Please enter a post description');
    return;
  }

  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error('User not authenticated');

    setLoading(true);
    let imageUrl = newPost.imageUrl;

    // If there's an image file, upload it
    if (newPost.imageFile) {
      imageUrl = await BunnyUploader.upload(newPost.imageFile);
    }

    const postData = {
      description: newPost.description,
      imageUrl: imageUrl,
      authorName: user.displayName || 'Admin',
      authorAvatarUrl: user.photoURL || 'https://i.ibb.co/QDgYxYm/user.jpg',
      authorId: user.uid,
      createdAt: new Date(),
      scheduledAt: newPost.scheduledAt ? new Date(newPost.scheduledAt) : null,
      likes: 0,
      likedBy: [],
      comments: 0,
    };

    await addDoc(postsColRef, postData);
    
    setNewPost({
      description: '',
      imageUrl: '',
      imageFile: null,
      scheduledAt: ''
    });
    loadData();
    toast.success('Post added successfully!');
  } catch (error) {
    console.error("Error adding post:", error);
    toast.error(`Error adding post: ${error.message}`);
  } finally {
    setLoading(false);
  }
};

const handlePostUpdate = async (postId, updatedData) => {
  try {
    await updateDoc(doc(postsColRef, postId), updatedData);
    loadData();
    setEditingPost(null);
    setEditingDraft({ description: '', imageUrl: '' });
  } catch (error) {
    console.error("Error updating post:", error);
  }
};

const handlePostDelete = async (postId) => {
  if (!window.confirm('Are you sure you want to delete this post?')) return;
  
  try {
    await deleteDoc(doc(postsColRef, postId));
    loadData();
  } catch (error) {
    console.error("Error deleting post:", error);
  }
};

const toggleLike = async (post) => {
  const user = auth.currentUser;
  if (!user) return;

  try {
    const isLiked = post.likedBy.includes(user.uid);
    const newLikedBy = isLiked 
      ? post.likedBy.filter(id => id !== user.uid) 
      : [...post.likedBy, user.uid];

    await updateDoc(doc(postsColRef, post.id), {
      likes: isLiked ? post.likes - 1 : post.likes + 1,
      likedBy: newLikedBy,
    });

    loadData();
  } catch (error) {
    console.error("Error toggling like:", error);
  }
};

  const removePollOption = (index) => {
    if (newPoll.options.length <= 1) return;
    const options = [...newPoll.options];
    options.splice(index, 1);
    setNewPoll({ ...newPoll, options });
  };

  const endActivePoll = async () => {
    if (!activePoll) return;

    try {
      // Archive the active poll
      await addDoc(pollsColRef, {
        ...activePoll,
        endedAt: new Date(),
        status: 'archived',
        totalVotes: activePoll.options.reduce((sum, opt) => sum + opt.votes, 0),
      });

      // Delete the active poll
      await deleteDoc(activePollDocRef);

      // Clear active poll state
      setActivePoll(null);
      setHasVoted(false);

      loadData();
    } catch (error) {
      console.error("Error ending poll:", error);
    }
  };

  const vote = async (selectedKey) => {
    const user = auth.currentUser;
    if (!user || !activePoll) return;
    
    if (hasVoted) {
      toast.info("You have already voted in this poll!");
      return;
    }

    try {
      // Update the vote count
      const updatedOptions = activePoll.options.map(opt =>
        opt.key === selectedKey ? { ...opt, votes: opt.votes + 1 } : opt
      );

      await setDoc(activePollDocRef, {
        ...activePoll,
        options: updatedOptions,
        totalVotes: activePoll.totalVotes + 1,
      }, { merge: true });

      // Record the user's vote
      await setDoc(doc(db, 'subscriber_club', 'clubData', 'pollVotes', user.uid), {
        pollId: 'active', // Reference to the active poll
        votedOption: selectedKey,
        timestamp: new Date(),
      });

      setHasVoted(true);
      loadData();
    } catch (error) {
      console.error("Error voting:", error);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
       {/* Header Section */}
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Subscriber Club Manager</h1>
        <p className="text-gray-600 mt-2">Manage all subscriber-exclusive content and features</p>
      </header>

      {/* Club Data Section */}
 {/* Club Configuration Section */}
      <section className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center border-b pb-4 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Club Configuration</h2>
          <button
            onClick={updateClubDoc}
            className="bg-black hover:bg-gray-800 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
          >
            Save Configuration
          </button>
        </div>

        {/* Welcome Message */}
        <div className="mb-6">
          <label className="block text-lg font-medium text-gray-700 mb-2">Welcome Message</label>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Welcome your subscribers with a friendly message..."
            value={form.welcomeMessage}
            onChange={(e) => setForm({ ...form, welcomeMessage: e.target.value })}
            rows={3}
          />
        </div>

        {/* Quote of the Week */}
        <div className="mb-6">
          <label className="block text-lg font-medium text-gray-700 mb-2">Quote of the Week</label>
          <textarea
            className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Inspire your subscribers with a weekly quote..."
            value={form.quoteOfWeek}
            onChange={(e) => setForm({ ...form, quoteOfWeek: e.target.value })}
            rows={2}
          />
        </div>
      </section>

      {/* Sneak Peek Management Section (NEW MULTIPLE LIST) */}
      <section className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center border-b pb-4 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Sneak Peek Content</h2>
          <button
            onClick={() => {
              const newSneakPeek = { id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(), imageUrl: '', audioUrl: '', scheduledAt: '' };
              setForm({ ...form, sneakPeeks: [...(form.sneakPeeks || []), newSneakPeek] });
            }}
            className="bg-black hover:bg-gray-800 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
          >
            + Add Sneak Peek
          </button>
        </div>
        
        {(form.sneakPeeks || []).length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <p className="text-gray-500 text-lg">No sneak peeks added yet.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {(form.sneakPeeks || []).map((sp, idx) => (
              <div key={sp.id} className="border border-gray-200 p-6 rounded-xl bg-gray-50/50">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Sneak Peek #{idx + 1}</h3>
                  <button
                    onClick={() => {
                      const updated = form.sneakPeeks.filter((_, i) => i !== idx);
                      setForm({ ...form, sneakPeeks: updated });
                    }}
                    className="text-red-500 hover:text-red-700 font-medium"
                  >
                    Delete
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Image */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview Image</label>
                    <div className="flex gap-2">
                       <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const updated = [...form.sneakPeeks];
                            updated[idx] = { ...updated[idx], imageFile: file, imageUrl: URL.createObjectURL(file) };
                            setForm({ ...form, sneakPeeks: updated });
                          }
                        }}
                        className="hidden"
                        id={`sneakPeekImage-${idx}`}
                      />
                      <label htmlFor={`sneakPeekImage-${idx}`} className="bg-gray-200 px-3 py-2 rounded cursor-pointer text-sm">Upload</label>
                      <input
                        className="flex-1 border rounded px-3 py-2 text-sm"
                        placeholder="Image URL"
                        value={sp.imageUrl || ''}
                        onChange={(e) => {
                          const updated = [...form.sneakPeeks];
                          updated[idx] = { ...updated[idx], imageUrl: e.target.value, imageFile: null };
                          setForm({ ...form, sneakPeeks: updated });
                        }}
                      />
                    </div>
                    {sp.imageUrl && <img src={sp.imageUrl} className="mt-2 h-32 object-cover rounded" />}
                  </div>

                  {/* Audio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preview Audio</label>
                    <div className="flex gap-2">
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            const ext = file.name.toLowerCase().split('.').pop();
                            if (ext === 'aiff' || ext === 'aif') {
                              toast.error("AIFF files are not supported on Android. Please upload MP3 or WAV.");
                              e.target.value = '';
                              return;
                            }
                            const updated = [...form.sneakPeeks];
                            updated[idx] = { ...updated[idx], audioFile: file, audioUrl: URL.createObjectURL(file) };
                            setForm({ ...form, sneakPeeks: updated });
                          }
                        }}
                        className="hidden"
                        id={`sneakPeekAudio-${idx}`}
                      />
                      <label htmlFor={`sneakPeekAudio-${idx}`} className="bg-gray-200 px-3 py-2 rounded cursor-pointer text-sm">Upload</label>
                      <input
                        className="flex-1 border rounded px-3 py-2 text-sm"
                        placeholder="Audio URL"
                        value={sp.audioUrl || ''}
                        onChange={(e) => {
                          const updated = [...form.sneakPeeks];
                          updated[idx] = { ...updated[idx], audioUrl: e.target.value, audioFile: null };
                          setForm({ ...form, sneakPeeks: updated });
                        }}
                      />
                    </div>
                    {sp.audioUrl && <audio src={sp.audioUrl} controls className="mt-2 w-full" />}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date & Time</label>
                  <input
                    type="datetime-local"
                    className="w-full border p-2 rounded text-sm"
                    value={sp.scheduledAt || ''}
                    onChange={(e) => {
                      const updated = [...form.sneakPeeks];
                      updated[idx] = { ...updated[idx], scheduledAt: e.target.value };
                      setForm({ ...form, sneakPeeks: updated });
                    }}
                    min={new Date().toISOString().slice(0, 16)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

       {/* Holiday Special Section */}
      <section className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            id="holidayToggle"
            checked={form.isHolidaySpecial}
            onChange={(e) => setForm({ ...form, isHolidaySpecial: e.target.checked })}
            className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="holidayToggle" className="text-lg font-semibold text-gray-800 cursor-pointer">
            Enable Holiday Special
          </label>
        </div>
        
        {form.isHolidaySpecial && (
          <div className="bg-yellow-50 p-5 rounded-lg border border-yellow-200 space-y-4">
            <div>
              <label className="block text-lg font-medium text-gray-700 mb-2">Holiday Title</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Special holiday title..."
                value={form.holidaySpecial.title}
                onChange={(e) => setForm({
                  ...form,
                  holidaySpecial: { ...form.holidaySpecial, title: e.target.value }
                })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Holiday Image */}
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">Holiday Image</label>
                <div className="flex gap-3 items-center">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const previewUrl = URL.createObjectURL(file);
                        setForm({
                          ...form,
                          holidaySpecial: {
                            ...form.holidaySpecial,
                            imageFile: file,
                            imageUrl: previewUrl
                          }
                        });
                      }
                    }}
                    className="hidden"
                    id="holidayImageUpload"
                  />
                  <label
                    htmlFor="holidayImageUpload"
                    className="bg-yellow-100 hover:bg-yellow-200 px-4 py-2.5 rounded-lg cursor-pointer border border-yellow-300 transition-colors font-medium"
                  >
                    {form.holidaySpecial.imageFile ? 'Change Image' : 'Upload Image'}
                  </label>
                  <input
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Or paste image URL"
                    value={form.holidaySpecial.imageUrl}
                    onChange={(e) => setForm({
                      ...form,
                      holidaySpecial: {
                        ...form.holidaySpecial,
                        imageUrl: e.target.value,
                        imageFile: null
                      }
                    })}
                  />
                </div>
                {form.holidaySpecial.imageUrl && (
                  <div className="mt-3">
                    <img src={form.holidaySpecial.imageUrl} alt="Holiday Preview" className="max-h-60 rounded-lg border shadow-sm" />
                  </div>
                )}
              </div>

              {/* Holiday Audio */}
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">Holiday Audio</label>
                <div className="flex gap-3 items-center">
                  <input
                    type="file"
                    accept="audio/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const ext = file.name.toLowerCase().split('.').pop();
                        if (ext === 'aiff' || ext === 'aif') {
                          toast.error("AIFF files are not supported. Please use MP3 or WAV.");
                          e.target.value = "";
                          return;
                        }
                        const previewUrl = URL.createObjectURL(file);
                        setForm({
                          ...form,
                          holidaySpecial: {
                            ...form.holidaySpecial,
                            audioFile: file,
                            audioUrl: previewUrl
                          }
                        });
                      }
                    }}
                    className="hidden"
                    id="holidayAudioUpload"
                  />
                  <label
                    htmlFor="holidayAudioUpload"
                    className="bg-yellow-100 hover:bg-yellow-200 px-4 py-2.5 rounded-lg cursor-pointer border border-yellow-300 transition-colors font-medium"
                  >
                    {form.holidaySpecial.audioFile ? 'Change Audio' : 'Upload Audio'}
                  </label>
                  <input
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Or paste audio URL"
                    value={form.holidaySpecial.audioUrl}
                    onChange={(e) => setForm({
                      ...form,
                      holidaySpecial: {
                        ...form.holidaySpecial,
                        audioUrl: e.target.value,
                        audioFile: null
                      }
                    })}
                  />
                </div>
                {form.holidaySpecial.audioUrl && (
                  <div className="mt-3">
                    <audio controls src={form.holidaySpecial.audioUrl} className="w-full rounded-lg" />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date & Time (optional)</label>
              <input
                type="datetime-local"
                className="w-full border p-2 rounded"
                value={form.holidaySpecial.scheduledAt || ''}
                onChange={(e) => setForm({
                  ...form,
                  holidaySpecial: { ...form.holidaySpecial, scheduledAt: e.target.value }
                })}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          </div>
        )}
      </section>

      {/* Bonus Scenes Section */}
      {/* Bonus Scenes Section */}
      <section className="bg-white rounded-xl shadow-md p-6">
        <div className="flex justify-between items-center border-b pb-4 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Bonus Scenes</h2>
          <button
            onClick={handleSceneAdd}
            className="bg-black hover:bg-gray-800 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
          >
            + Add Scene
          </button>
        </div>

        {bonusScenes.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <p className="text-gray-500 text-lg">No bonus scenes added yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {bonusScenes.map((scene) => (
              <BonusSceneItem 
                key={scene.id} 
                scene={scene} 
                onUpdate={handleSceneUpdate} 
                onDelete={handleSceneDelete}
                books={books}
              />
            ))}
          </div>
        )}
      </section>

      {/* Poll Management Section */}
       {/* Poll Management Section */}
      <section className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-2xl font-semibold text-gray-800 border-b pb-4 mb-6">Poll Management</h2>
        
        {/* Create New Poll */}
        <div className="border border-gray-200 p-6 rounded-xl mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Create New Poll</h3>
          <input
            className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Enter your poll question..."
            value={newPoll.question}
            onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })}
          />
          
          <div className="flex items-center gap-3 mb-4">
            <input
              type="checkbox"
              checked={newPoll.multiChoice}
              onChange={(e) => setNewPoll({ ...newPoll, multiChoice: e.target.checked })}
              className="h-5 w-5 rounded text-blue-600 focus:ring-blue-500"
            />
            <label className="text-lg font-medium text-gray-700">Allow multiple choice</label>
          </div>
          
          
          <div className="space-y-3 mb-6">
            {newPoll.options.map((opt, i) => (
              <div key={i} className="flex gap-3 items-center">
                <span className="font-bold text-lg w-6">{opt.key}:</span>
                <input
                  className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`Option ${opt.key}`}
                  value={opt.label}
                  onChange={(e) => updatePollOption(i, 'label', e.target.value)}
                />
                <button
                  className="text-red-500 hover:text-red-700 w-9 h-9 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors"
                  onClick={() => removePollOption(i)}
                  disabled={newPoll.options.length <= 1}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
                <div className="mt-4 mb-6">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Scheduled Date & Time (optional)
    </label>
    <input
      type="datetime-local"
      className="w-full border p-2 rounded"
      value={newPoll.scheduledAt}
      onChange={(e) => setNewPoll({
        ...newPoll,
        scheduledAt: e.target.value
      })}
      min={new Date().toISOString().slice(0, 16)}
    />
  </div>
          
          <div className="flex gap-4">
            <button
              className="bg-gray-200 hover:bg-gray-300 px-5 py-2.5 rounded-lg transition-colors font-medium"
              onClick={addPollOption}
            >
              + Add Option
            </button>
            
            {/* Add this date picker before the create button */}
  

            <button
              className="bg-black hover:bg-gray-800 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
              onClick={handlePollAdd}
              disabled={!newPoll.question || newPoll.options.some(opt => !opt.label)}
            >
              Create Poll
            </button>
          </div>
    
        </div>
        

        {/* Active Poll Display */}
        {activePoll && (
          <div className="border border-yellow-200 bg-yellow-50 p-6 rounded-xl mb-8">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-800">Active Poll</h3>
              <button
                onClick={endActivePoll}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                End Poll
              </button>
            </div>
            
            <p className="text-lg font-medium text-gray-800 mb-4">{activePoll.question}</p>
            
            <ul className="space-y-3 mb-4">
              {activePoll.options.map((opt, i) => (
                <li key={i} className="flex justify-between items-center bg-white p-3 rounded-lg border border-gray-200">
                  <span className="flex items-center gap-2">
                    <span className="font-bold">{opt.key}:</span> 
                    <span>{opt.label}</span>
                    <span className="text-gray-500 text-sm">({opt.votes} votes)</span>
                  </span>
                  {!hasVoted && (
                    <button
                      onClick={() => vote(opt.key)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
                    >
                      Vote
                    </button>
                  )}
                </li>
              ))}
            </ul>
            
            {activePoll && (
              <p className="text-green-600 font-medium mb-2">✓ Active poll is live and visible to subscribers</p>
            )}
            
            <div className="text-gray-600">
              Total votes: <span className="font-medium">{activePoll.options.reduce((sum, opt) => sum + opt.votes, 0)}</span>
            </div>
          </div>
        )}

        {/* Archived Polls */}
        {polls.length > 0 && (
          <div className="border border-gray-200 p-6 rounded-xl">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Poll History</h3>
            <div className="space-y-4">
              {polls.map((poll, index) => (
                <details key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <summary className="bg-gray-50 p-4 cursor-pointer list-none">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-800">
                        {poll.question} ({new Date(poll.createdAt?.seconds * 1000).toLocaleDateString()})
                      </span>
                      <svg className="w-5 h-5 text-gray-500 transform transition-transform duration-200" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </summary>
                  <div className="p-4 bg-white">
                    <ul className="space-y-2">
                      {poll.options?.map((opt, i) => (
                        <li key={i} className="flex justify-between">
                          <span>
                            <span className="font-medium">{opt.key}:</span> {opt.label}
                          </span>
                          <span className="text-gray-600">{opt.votes} votes</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-3 text-sm text-gray-600">
                      Total votes: {poll.options?.reduce((sum, opt) => sum + opt.votes, 0)}
                    </div>
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Posts Section */}
      <div className="bg-white p-4 rounded shadow space-y-4">
        <h2 className="text-xl font-semibold">Social Posts</h2>

        {/* Create New Post */}
  <div className="border p-4 rounded-lg bg-gray-50">
    <h3 className="font-bold mb-2">Create New Post</h3>
    
    <textarea
      className="w-full border px-4 py-2 rounded mb-2 min-h-[100px]"
      placeholder="What's on your mind?"
      value={newPost.description}
      onChange={(e) => setNewPost({...newPost, description: e.target.value})}
    />
    
    <div className="flex gap-2 items-center mb-4">
  <input
    type="file"
    accept="image/*"
    onChange={async (e) => {
      const file = e.target.files?.[0];
      if (file) {
        // Show preview while keeping the original file for upload
        const previewUrl = URL.createObjectURL(file);
        setNewPost({
          ...newPost,
          imageFile: file,
          imageUrl: previewUrl,
        });
      }
    }}
    className="hidden"
    id="postImageUpload"
  />
  <label
    htmlFor="postImageUpload"
    className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded cursor-pointer"
  >
    Upload Image
  </label>
  <input
    className="flex-1 border px-4 py-2 rounded"
    placeholder="Or enter image URL"
    value={newPost.imageUrl}
    onChange={(e) => setNewPost({
      ...newPost,
      imageUrl: e.target.value,
      imageFile: null, // Clear file if URL is entered manually
    })}
  />
</div>
    
    {newPost.imageUrl && (
      <div className="mb-4">
        <img 
          src={newPost.imageUrl} 
          alt="Post preview" 
          className="max-h-40 rounded border"
        />
      </div>
    )}
    {/* Add this date picker before the post button */}
  <div className="mt-4 mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1">
      Scheduled Date & Time (optional)
    </label>
    <input
      type="datetime-local"
      className="w-full border p-2 rounded"
      value={newPost.scheduledAt}
      onChange={(e) => setNewPost({
        ...newPost,
        scheduledAt: e.target.value
      })}
      min={new Date().toISOString().slice(0, 16)}
    />
  </div>

    {loading ? (
  <button
    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center justify-center"
    disabled
  >
    <svg
      className="animate-spin h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
      ></path>
    </svg>
  </button>
) : (
  <button
    onClick={async () => {
      setLoading(true);
      await handlePostAdd();
      setLoading(false);
    }}
    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
    disabled={!newPost.description}
  >
    Post
  </button>
)}
  </div>
  
  {/* Posts List */}
  <div className="space-y-4">
    {posts.map((post) => (
      <div key={post.id} className="border rounded-lg p-4">
        {/* Post Header */}
        <div className="flex items-center gap-3 mb-3">
          <img 
            src={post.authorAvatarUrl} 
            alt="Author" 
            className="w-10 h-10 rounded-full"
          />
          <div>
            <p className="font-semibold">{post.authorName}</p>
            <p className="text-xs text-gray-500">
              {new Date(post.createdAt?.seconds * 1000).toLocaleString()}
            </p>
          </div>
        </div>
        
        {/* Post Content */}
        <p className="mb-3">{post.description}</p>
        
        {post.imageUrl && (
          <div className="mb-3">
            <img 
              src={post.imageUrl} 
              alt="Post" 
              className="max-w-full rounded-lg border"
            />
          </div>
        )}
        
        {/* Post Actions */}
        <div className="flex justify-between items-center border-t pt-3">
          <button 
            onClick={() => toggleLike(post)}
            className={`flex items-center gap-1 ${post.likedBy?.includes(auth.currentUser?.uid) ? 'text-red-500' : 'text-gray-500'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
            </svg>
            <span>{post.likes || 0}</span>
          </button>
          
          <div className="flex gap-2">
            <button 
              onClick={() => {
                if (editingPost === post.id) {
                  setEditingPost(null);
                  setEditingDraft({ description: '', imageUrl: '' });
                } else {
                  setEditingPost(post.id);
                  setEditingDraft({
                    description: post.description || '',
                    imageUrl: post.imageUrl || '',
                  });
                }
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
            </button>
            <button 
              onClick={() => handlePostDelete(post.id)}
              className="text-gray-500 hover:text-red-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Edit Post Form (conditionally shown) */}
        {editingPost === post.id && (
          <div className="mt-4 border-t pt-4">
            <textarea
              className="w-full border px-4 py-2 rounded mb-2 min-h-[100px]"
              value={editingDraft.description}
              onChange={(e) =>
                setEditingDraft((prev) => ({ ...prev, description: e.target.value }))
              }
            />
            <input
              className="w-full border px-4 py-2 rounded mb-2"
              placeholder="Image URL"
              value={editingDraft.imageUrl}
              onChange={(e) =>
                setEditingDraft((prev) => ({ ...prev, imageUrl: e.target.value }))
              }
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditingPost(null)}
                className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handlePostUpdate(post.id, {
                    description: editingDraft.description,
                    imageUrl: editingDraft.imageUrl,
                  })
                }
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        )}
      </div>
    ))}
    
    {posts.length === 0 && (
      <p className="text-center text-gray-500 py-4">No posts yet. Create the first one!</p>
    )}
  </div>
</div>
      </div>
    );
  }